import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, Tabs, Tab, TextField,
  Paper, useTheme, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Autocomplete, Popover, Chip,
} from '@mui/material';
import {
  Add, Delete, ArrowBack, Save, Download, Upload,
  ViewColumn, TableRows, Close, ContentCopy, Link as LinkIcon,
} from '@mui/icons-material';
import { getApiToken } from '../../services/authConfig';
import { parseCSV } from '../../utils/csv';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || '';

const DEFAULT_COLS = 8;
const DEFAULT_ROWS = 20;

// Spreadsheets to cross-reference for VLOOKUP (Feature 3)
const CROSS_DBS = [
  { key: 'db_projects', title: 'מאגר מידע פרויקטים' },
  { key: 'db_contacts', title: 'מאגר אנשי מפתח' },
];

const createEmptySheet = (name = 'גיליון 1') => ({
  name,
  headers: Array.from({ length: DEFAULT_COLS }, (_, i) => `עמודה ${i + 1}`),
  rows: Array.from({ length: DEFAULT_ROWS }, () => Array(DEFAULT_COLS).fill('')),
});

const SpreadsheetPage = ({ storageKey, title, onBack }) => {
  const theme = useTheme();
  const [sheets, setSheets] = useState([createEmptySheet()]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingHeader, setEditingHeader] = useState(null);
  const [newSheetDialog, setNewSheetDialog] = useState(false);
  const [newSheetName, setNewSheetName] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);
  const [cellDialog, setCellDialog] = useState(null);
  const [importDialog, setImportDialog] = useState(null); // { headers, rows }

  // Feature 3: cross-database data + matches
  const [crossData, setCrossData] = useState({});
  const [crossLoaded, setCrossLoaded] = useState(false);
  const [crossMatches, setCrossMatches] = useState({}); // { 'ri:ci': match }
  const [crossPopover, setCrossPopover] = useState(null); // { anchorEl, match }

  const tableRef = useRef(null);
  const csvInputRef = useRef(null);
  const revertTimer = useRef(null);

  const sheet = sheets[activeSheet] || sheets[0];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getApiToken();
      const res = await fetch(`${API_BASE}/spreadsheets/${encodeURIComponent(storageKey)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.sheets && data.sheets.length > 0) {
          setSheets(data.sheets);
        } else {
          setSheets([createEmptySheet()]);
        }
      } else {
        setSheets([createEmptySheet()]);
      }
    } catch (err) {
      console.log('No existing data, using defaults');
      setSheets([createEmptySheet()]);
    } finally {
      setLoading(false);
    }
  }, [storageKey]);

  useEffect(() => {
    setActiveSheet(0);
    setSheets([createEmptySheet()]);
    setSelectedCell(null);
    loadData();
  }, [storageKey, loadData]);

  // ===== Feature 3: load cross-reference spreadsheets on mount / key change =====
  useEffect(() => {
    let cancelled = false;
    setCrossLoaded(false);
    (async () => {
      try {
        const token = await getApiToken();
        const result = {};
        for (const db of CROSS_DBS) {
          try {
            const res = await fetch(`${API_BASE}/spreadsheets/${encodeURIComponent(db.key)}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              result[db.key] = { title: db.title, sheets: data.sheets || [] };
            }
          } catch (e) {
            /* skip this DB */
          }
        }
        if (!cancelled) {
          setCrossData(result);
          setCrossLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setCrossLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [storageKey]);

  // Find a matching row for a value in ANY other spreadsheet (case-insensitive, trimmed)
  const findCrossMatch = useCallback((value) => {
    const needle = String(value || '').trim().toLowerCase();
    if (needle.length < 2) return null;
    for (const db of CROSS_DBS) {
      if (db.key === storageKey) continue; // don't match against the current sheet
      const data = crossData[db.key];
      if (!data || !Array.isArray(data.sheets)) continue;
      for (const s of data.sheets) {
        for (const row of s.rows) {
          for (let ci = 0; ci < row.length; ci += 1) {
            const cellVal = String(row[ci] || '').trim().toLowerCase();
            if (cellVal && cellVal === needle) {
              return { dbTitle: data.title, sheetName: s.name, headers: s.headers, row };
            }
          }
        }
      }
    }
    return null;
  }, [crossData, storageKey]);

  // Recompute cross matches for the active sheet, debounced (500ms)
  useEffect(() => {
    if (!crossLoaded) return undefined;
    const timer = setTimeout(() => {
      const matches = {};
      sheet.rows.forEach((row, ri) => {
        row.forEach((cell, ci) => {
          const m = findCrossMatch(cell);
          if (m) matches[`${ri}:${ci}`] = m;
        });
      });
      setCrossMatches(matches);
    }, 500);
    return () => clearTimeout(timer);
  }, [sheet, crossLoaded, findCrossMatch]);

  // ===== SAVE =====
  const persistSheets = useCallback(async (sheetsToSave) => {
    try {
      setSaving(true);
      const token = await getApiToken();
      await fetch(`${API_BASE}/spreadsheets/${encodeURIComponent(storageKey)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        // Base64 encoding for safe transport of Hebrew/Unicode text through WAF
        body: JSON.stringify({ payload: btoa(unescape(encodeURIComponent(JSON.stringify({ sheets: sheetsToSave })))) }),
      });
      toast.success('נשמר בהצלחה');
    } catch (err) {
      toast.error('שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  }, [storageKey]);

  const saveData = useCallback(() => persistSheets(sheets), [persistSheets, sheets]);

  const updateCell = (rowIdx, colIdx, value) => {
    setSheets(prev => {
      const updated = [...prev];
      const s = { ...updated[activeSheet] };
      s.rows = s.rows.map((r, ri) =>
        ri === rowIdx ? r.map((c, ci) => ci === colIdx ? value : c) : r
      );
      updated[activeSheet] = s;
      return updated;
    });
  };

  const updateHeader = (colIdx, value) => {
    setSheets(prev => {
      const updated = [...prev];
      const s = { ...updated[activeSheet] };
      s.headers = s.headers.map((h, i) => i === colIdx ? value : h);
      updated[activeSheet] = s;
      return updated;
    });
  };

  const addRow = () => {
    setSheets(prev => {
      const updated = [...prev];
      const s = { ...updated[activeSheet] };
      s.rows = [...s.rows, Array(s.headers.length).fill('')];
      updated[activeSheet] = s;
      return updated;
    });
  };

  const addColumn = () => {
    setSheets(prev => {
      const updated = [...prev];
      const s = { ...updated[activeSheet] };
      s.headers = [...s.headers, `עמודה ${s.headers.length + 1}`];
      s.rows = s.rows.map(r => [...r, '']);
      updated[activeSheet] = s;
      return updated;
    });
  };

  const deleteRow = (rowIdx) => {
    setSheets(prev => {
      const updated = [...prev];
      const s = { ...updated[activeSheet] };
      s.rows = s.rows.filter((_, i) => i !== rowIdx);
      updated[activeSheet] = s;
      return updated;
    });
  };

  const copyRow = (rowIdx) => {
    setSheets(prev => {
      const updated = [...prev];
      const s = { ...updated[activeSheet] };
      const copiedRow = [...s.rows[rowIdx]];
      s.rows = [...s.rows.slice(0, rowIdx + 1), copiedRow, ...s.rows.slice(rowIdx + 1)];
      updated[activeSheet] = s;
      return updated;
    });
  };

  const deleteColumn = (colIdx) => {
    if (sheet.headers.length <= 1) return;
    setSheets(prev => {
      const updated = [...prev];
      const s = { ...updated[activeSheet] };
      s.headers = s.headers.filter((_, i) => i !== colIdx);
      s.rows = s.rows.map(r => r.filter((_, i) => i !== colIdx));
      updated[activeSheet] = s;
      return updated;
    });
  };

  const addSheet = () => {
    const name = newSheetName.trim() || `גיליון ${sheets.length + 1}`;
    setSheets(prev => [...prev, createEmptySheet(name)]);
    setActiveSheet(sheets.length);
    setNewSheetDialog(false);
    setNewSheetName('');
  };

  const deleteSheet = (idx) => {
    if (sheets.length <= 1) return;
    if (!window.confirm(`למחוק את "${sheets[idx].name}"?`)) return;
    setSheets(prev => prev.filter((_, i) => i !== idx));
    if (activeSheet >= sheets.length - 1) setActiveSheet(Math.max(0, sheets.length - 2));
  };

  const exportCSV = () => {
    const escapeCsv = (val) => {
      const s = String(val || '');
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const csv = [
      sheet.headers.map(escapeCsv).join(','),
      ...sheet.rows.map(r => r.map(escapeCsv).join(','))
    ].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title || storageKey}-${sheet.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ===== Feature 1A: CSV IMPORT =====
  const handleCsvButtonClick = () => {
    csvInputRef.current?.click();
  };

  const handleCsvFileSelect = (event) => {
    const file = event.target.files[0];
    event.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const { headers, rows } = parseCSV(e.target.result);
        if (!headers.length) {
          toast.error('קובץ CSV ריק או לא תקין');
          return;
        }
        setImportDialog({ headers, rows });
      } catch (err) {
        console.error(err);
        toast.error('שגיאה בקריאת הקובץ');
      }
    };
    reader.onerror = () => toast.error('שגיאה בקריאת הקובץ');
    reader.readAsText(file, 'UTF-8');
  };

  const applyImport = (mode) => {
    if (!importDialog) return;
    const { headers: csvHeaders, rows: csvRows } = importDialog;
    const updated = [...sheets];
    const s = { ...updated[activeSheet] };
    let importedCount = 0;

    if (mode === 'replace') {
      const colCount = csvHeaders.length;
      s.headers = csvHeaders.map(h => String(h != null ? h : ''));
      s.rows = csvRows.map(r =>
        Array.from({ length: colCount }, (_, i) => (r[i] != null ? String(r[i]) : ''))
      );
      importedCount = s.rows.length;
    } else {
      // add: keep current headers, map CSV rows by column position
      const colCount = s.headers.length;
      const mapped = csvRows.map(r =>
        Array.from({ length: colCount }, (_, i) => (r[i] != null ? String(r[i]) : ''))
      );
      s.rows = [...s.rows, ...mapped];
      importedCount = mapped.length;
    }

    updated[activeSheet] = s;
    setSheets(updated);
    setSelectedCell(null);
    setImportDialog(null);
    persistSheets(updated);
    toast.success(`${importedCount} שורות יובאו בהצלחה`);
  };

  // ===== Feature 2: unique column values for cell autocomplete =====
  const getColumnValues = (colIdx) => {
    const set = new Set();
    sheet.rows.forEach(r => {
      const v = r[colIdx];
      if (v != null && String(v).trim() !== '') set.add(String(v));
    });
    return Array.from(set);
  };

  // Revert active cell back to display after blur (unless another cell grabs focus)
  const scheduleRevert = () => {
    if (revertTimer.current) clearTimeout(revertTimer.current);
    revertTimer.current = setTimeout(() => setSelectedCell(null), 250);
  };
  const cancelRevert = () => {
    if (revertTimer.current) {
      clearTimeout(revertTimer.current);
      revertTimer.current = null;
    }
  };

  const handleKeyDown = (e, rowIdx, colIdx) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const nextCol = e.shiftKey ? colIdx - 1 : colIdx + 1;
      if (nextCol >= 0 && nextCol < sheet.headers.length) {
        setSelectedCell({ row: rowIdx, col: nextCol });
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const nextRow = rowIdx + 1;
      if (nextRow < sheet.rows.length) {
        setSelectedCell({ row: nextRow, col: colIdx });
      } else {
        addRow();
        setTimeout(() => setSelectedCell({ row: nextRow, col: colIdx }), 50);
      }
    }
  };

  const handleCellDoubleClick = (rowIdx, colIdx) => {
    setCellDialog({ row: rowIdx, col: colIdx, value: sheet.rows[rowIdx][colIdx] || '' });
  };

  const saveCellDialog = () => {
    if (cellDialog) {
      updateCell(cellDialog.row, cellDialog.col, cellDialog.value);
      setCellDialog(null);
    }
  };

  // ===== Render a single editable cell (active state) =====
  const renderActiveEditor = (ri, ci, cell, cellHeight) => {
    const options = getColumnValues(ci);

    // Feature 2: use Autocomplete when the column has at least one known value
    if (options.length >= 1) {
      return (
        <Autocomplete
          freeSolo
          openOnFocus
          disableClearable
          fullWidth
          size="small"
          options={options}
          inputValue={cell || ''}
          onInputChange={(_, val) => updateCell(ri, ci, val)}
          onChange={(_, val) => updateCell(ri, ci, typeof val === 'string' ? val : (val || ''))}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              variant="standard"
              onKeyDown={(e) => {
                if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
                  handleKeyDown(e, ri, ci);
                }
              }}
              onFocus={cancelRevert}
              onBlur={scheduleRevert}
              InputProps={{ ...params.InputProps, disableUnderline: true }}
              inputProps={{
                ...params.inputProps,
                dir: 'rtl',
                style: {
                  padding: '6px 8px', fontSize: '0.8rem', textAlign: 'right',
                  fontFamily: 'inherit',
                },
              }}
            />
          )}
        />
      );
    }

    // Fallback: plain textarea when there are no known values yet
    return (
      <textarea
        value={cell}
        autoFocus
        onChange={(e) => updateCell(ri, ci, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
            handleKeyDown(e, ri, ci);
          }
        }}
        onFocus={cancelRevert}
        onBlur={scheduleRevert}
        rows={1}
        dir="rtl"
        style={{
          width: '100%', border: 'none', padding: '6px 8px',
          fontSize: '0.8rem', fontFamily: 'inherit', textAlign: 'right',
          background: 'transparent', outline: 'none', boxSizing: 'border-box',
          resize: 'none', overflow: 'hidden',
          minHeight: 30, height: cellHeight,
        }}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onBack && (
            <IconButton onClick={onBack} size="small">
              <ArrowBack />
            </IconButton>
          )}
          <Typography variant="h5" fontWeight={700}>{title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<Upload />} onClick={handleCsvButtonClick}>
            ייבוא CSV
          </Button>
          <Button variant="outlined" size="small" startIcon={<Download />} onClick={exportCSV}>
            ייצוא CSV
          </Button>
          <Button variant="contained" size="small" startIcon={<Save />} onClick={saveData} disabled={saving}>
            {saving ? 'שומר...' : 'שמור'}
          </Button>
        </Box>
      </Box>
      <input
        ref={csvInputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={handleCsvFileSelect}
      />

      <Typography variant="h6" fontWeight={600} color="primary" sx={{ mb: 1.5 }}>
        {sheet.name}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Tooltip title="הוסף שורה">
          <Button variant="outlined" size="small" startIcon={<TableRows />} onClick={addRow}>שורה</Button>
        </Tooltip>
        <Tooltip title="הוסף עמודה">
          <Button variant="outlined" size="small" startIcon={<ViewColumn />} onClick={addColumn}>עמודה</Button>
        </Tooltip>
      </Box>

      <Paper sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'auto', mb: 1, maxHeight: '65vh' }} ref={tableRef}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">טוען...</Typography>
          </Box>
        ) : (
          <table dir="rtl" style={{ borderCollapse: 'collapse', width: '100%', minWidth: sheet.headers.length * 140 }}>
            <thead>
              <tr>
                <th style={{
                  width: 50, padding: '8px 4px', background: '#1565C0', color: '#fff',
                  fontSize: '0.75rem', fontWeight: 600, position: 'sticky', top: 0, zIndex: 2,
                  borderLeft: '1px solid rgba(255,255,255,0.2)', textAlign: 'center',
                }}>#</th>
                {sheet.headers.map((h, ci) => (
                  <th key={ci} style={{
                    padding: '4px 8px', background: '#1565C0', position: 'sticky', top: 0, zIndex: 2,
                    borderLeft: '1px solid rgba(255,255,255,0.2)', minWidth: 120,
                    textAlign: 'right',
                  }}>
                    {editingHeader === ci ? (
                      <input
                        autoFocus
                        value={h}
                        onChange={(e) => updateHeader(ci, e.target.value)}
                        onBlur={() => setEditingHeader(null)}
                        onKeyDown={(e) => e.key === 'Enter' && setEditingHeader(null)}
                        style={{
                          width: '100%', border: 'none', background: '#fff',
                          padding: '4px 6px', borderRadius: 3, fontSize: '0.8rem',
                          fontFamily: 'inherit', textAlign: 'right', direction: 'rtl',
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                          onClick={() => setEditingHeader(ci)}
                        >
                          {h}
                        </Typography>
                        <IconButton size="small" onClick={() => deleteColumn(ci)} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.2 }}>
                          <Delete sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    )}
                  </th>
                ))}
                <th style={{ width: 70, background: '#1565C0', position: 'sticky', top: 0, zIndex: 2 }} />
              </tr>
            </thead>
            <tbody>
              {sheet.rows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  <td style={{
                    padding: '2px 6px', textAlign: 'center', fontSize: '0.7rem',
                    color: '#999', borderBottom: '1px solid #eee', borderLeft: '1px solid #eee',
                  }}>{ri + 1}</td>
                  {row.map((cell, ci) => {
                    const isActive = selectedCell?.row === ri && selectedCell?.col === ci;
                    const match = crossMatches[`${ri}:${ci}`];
                    const lineCount = cell ? cell.split('\n').length : 1;
                    const isLong = cell && (cell.length > 40 || lineCount > 1);
                    const cellHeight = isLong ? Math.min(lineCount * 20 + 14, 100) : 30;
                    return (
                      <td key={ci} style={{
                        padding: 0, borderBottom: '1px solid #eee', borderLeft: '1px solid #eee',
                        maxWidth: 250, verticalAlign: 'top', position: 'relative',
                      }}
                        onDoubleClick={() => handleCellDoubleClick(ri, ci)}
                        title={isLong ? 'לחץ פעמיים לעריכה מורחבת' : undefined}
                      >
                        {isActive ? (
                          renderActiveEditor(ri, ci, cell, cellHeight)
                        ) : (
                          <div
                            tabIndex={0}
                            onClick={() => { cancelRevert(); setSelectedCell({ row: ri, col: ci }); }}
                            onFocus={() => { cancelRevert(); setSelectedCell({ row: ri, col: ci }); }}
                            style={{
                              width: '100%', padding: '6px 8px', boxSizing: 'border-box',
                              fontSize: '0.8rem', textAlign: 'right', direction: 'rtl',
                              whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'hidden',
                              cursor: 'text', minHeight: 30, height: cellHeight, outline: 'none',
                            }}
                          >
                            {cell}
                          </div>
                        )}
                        {match && (
                          <Tooltip title={`נמצא ב${match.dbTitle}`}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCrossPopover({ anchorEl: e.currentTarget, match });
                              }}
                              sx={{
                                position: 'absolute', top: 0, left: 0, p: '1px',
                                color: '#1565C0', background: 'rgba(255,255,255,0.7)',
                                '&:hover': { background: 'rgba(255,255,255,0.95)' },
                              }}
                            >
                              <LinkIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ borderBottom: '1px solid #eee', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => copyRow(ri)} sx={{ opacity: 0.3, '&:hover': { opacity: 1 } }}>
                      <ContentCopy sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => deleteRow(ri)} sx={{ opacity: 0.3, '&:hover': { opacity: 1, color: '#C62828' } }}>
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
        <Tabs
          value={activeSheet}
          onChange={(_, v) => setActiveSheet(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, px: 2, fontSize: '0.8rem' } }}
        >
          {sheets.map((s, i) => (
            <Tab
              key={i}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{s.name}</span>
                  {sheets.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); deleteSheet(i); }}
                      sx={{ p: 0.2, ml: 0.5, '&:hover': { color: '#C62828' } }}
                    >
                      <Delete sx={{ fontSize: 12 }} />
                    </IconButton>
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
        <Tooltip title="גיליון חדש">
          <IconButton size="small" onClick={() => setNewSheetDialog(true)}>
            <Add fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Cross-reference popover (Feature 3) */}
      <Popover
        open={Boolean(crossPopover)}
        anchorEl={crossPopover?.anchorEl}
        onClose={() => setCrossPopover(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {crossPopover && (
          <Box sx={{ p: 2, maxWidth: 360, direction: 'rtl' }} dir="rtl">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LinkIcon sx={{ fontSize: 18, color: '#1565C0' }} />
              <Typography variant="subtitle2" fontWeight={700} color="primary">
                נמצא ב{crossPopover.match.dbTitle}
              </Typography>
            </Box>
            <Chip
              label={`גיליון: ${crossPopover.match.sheetName}`}
              size="small"
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {crossPopover.match.headers.map((h, i) => {
                const val = crossPopover.match.row[i];
                if (!val || !String(val).trim()) return null;
                return (
                  <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ minWidth: 90 }}>
                      {h}:
                    </Typography>
                    <Typography variant="body2">{val}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Popover>

      {/* CSV import mode dialog (Feature 1A) */}
      <Dialog open={importDialog !== null} onClose={() => setImportDialog(null)} maxWidth="xs" fullWidth dir="rtl">
        <DialogTitle>ייבוא CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            נמצאו {importDialog?.rows.length || 0} שורות ({importDialog?.headers.length || 0} עמודות).
            כיצד לייבא לגיליון "{sheet.name}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={() => setImportDialog(null)} color="inherit">ביטול</Button>
          <Button onClick={() => applyImport('add')} variant="outlined">הוסף לגיליון נוכחי</Button>
          <Button onClick={() => applyImport('replace')} variant="contained">החלף גיליון נוכחי</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newSheetDialog} onClose={() => setNewSheetDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>גיליון חדש</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth size="small" label="שם הגיליון"
            value={newSheetName} onChange={(e) => setNewSheetName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addSheet()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewSheetDialog(false)} color="inherit">ביטול</Button>
          <Button variant="contained" onClick={addSheet}>צור</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cellDialog !== null}
        onClose={() => setCellDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`, py: 1.5,
        }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {cellDialog ? `${sheet.headers[cellDialog.col]} — שורה ${cellDialog.row + 1}` : ''}
          </Typography>
          <IconButton size="small" onClick={() => setCellDialog(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={15}
            value={cellDialog?.value || ''}
            onChange={(e) => setCellDialog(prev => prev ? { ...prev, value: e.target.value } : null)}
            sx={{ mt: 1 }}
            inputProps={{ dir: 'rtl' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setCellDialog(null)} color="inherit">ביטול</Button>
          <Button variant="contained" onClick={saveCellDialog}>שמור</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpreadsheetPage;
